import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { reduxForm, Field } from 'redux-form'
import Form from '../common/Form'
import Checkbox from '../common/Checkbox'
import InputContainer from '../../containers/common/InputContainer'
import { Option } from '../common/Select'
import SelectContainer from '../../containers/common/SelectContainer'
import { Icon, Popover, Spin } from '../common/Ant'
import Button from '../common/Button'
import CancelButton from '../common/CancelButton'
import Divider from '../common/Divider'
import Legend from '../common/Legend'
import FormError from '../common/FormError'
import {
  isAlphaNumeric,
  isModuleId,
  isNumeric,
  isInteger,
  required,
} from '../../utils/validators'
import { toTitleCase } from '../../utils/textFormatters'
import {
  PRESSURE_MONITOR_STATUS,
  PRESSURE_MONITOR_MEASUREMENT_UNITS,
  ROLES,
} from '../../constants'

// Raw and Default exist in the enum, but for now a pressure monitor can
// only be created/edited with wc or Pa selected
const SELECTABLE_MEASUREMENT_UNITS = [1, 2]

const EditPressureMonitorForm = props => {
  const {
    handleSubmit,
    submitting,
    error,
    submitAction,
    assetLocations,
    isPressureMonitorLoading,
    userRole,
    initialValues,
  } = props

  const submit = handleSubmit(submitAction)
  const canViewAdminFields = [ROLES.PILLAR_ADMIN, ROLES.SUPER_ADMIN].indexOf(
    userRole
  ) !== -1
  const measurementUnitLabel =
    initialValues &&
    PRESSURE_MONITOR_MEASUREMENT_UNITS[initialValues.measurementUnits]

  return isPressureMonitorLoading ? (
    <Spin size="large" className="w-100 center mv5" />
  ) : (
    <section className="EditPressureMonitorForm">
      <Form onSubmit={submit}>
        <div className="flex-ns">
          <Field
            name="pillarId"
            type="text"
            component={InputContainer}
            label={
              <span>
                Pillar ID{' '}
                <Popover
                  content={
                    <div style={{ maxWidth: 260 }}>
                      Must be 3 letters followed by 2 numbers (e.g. PAA00).
                      Leave blank to auto-generate the next available ID.
                    </div>
                  }
                  trigger={['hover', 'click']}
                >
                  <Icon type="question-circle-o" className="pointer" />
                </Popover>
              </span>
            }
            validate={[isAlphaNumeric]}
            placeholder="PAA00"
            className="mr3-ns w-third-ns"
          />
          <Field
            name="moduleId"
            type="text"
            component={InputContainer}
            label={
              <span>
                Module ID{' '}
                <Popover
                  content={
                    <div style={{ maxWidth: 260 }}>
                      The 16 character serial number on the Pressure Monitor.
                    </div>
                  }
                  trigger={['hover', 'click']}
                >
                  <Icon type="question-circle-o" className="pointer" />
                </Popover>
              </span>
            }
            validate={[required, isModuleId]}
            placeholder="1a2b3c4d5e6f7890"
            className="mr3-ns w-third-ns"
          />
          <Field
            name="assetLocationId"
            component={SelectContainer}
            label="Asset Location"
            className="w-third-ns"
            props={{ placeholder: 'Select an Asset Location' }}
            filterable
          >
            {[{ id: 0, pillarId: '-- Unassigned --' }, ...assetLocations].map(
              x => (
                <Option value={x.id} key={x.id}>
                  {x.pillarId}
                </Option>
              )
            )}
          </Field>
        </div>
        <div className="flex-ns">
          <Field
            name="status"
            component={SelectContainer}
            label="Status"
            placeholder="Select a status"
            className="mr3-ns w-third-ns"
            filterable
          >
            {Object.keys(PRESSURE_MONITOR_STATUS).map(x => (
              <Option value={PRESSURE_MONITOR_STATUS[x]} key={x}>
                {toTitleCase(PRESSURE_MONITOR_STATUS[x])}
              </Option>
            ))}
          </Field>
          <Field
            name="description"
            type="text"
            component={InputContainer}
            label="Description"
            className="w-two-thirds-ns"
          />
        </div>
        <div className="flex-ns">
          <Field
            name="activateThreshold"
            type="number"
            component={InputContainer}
            label={
              <span>
                Activate Threshold
                {measurementUnitLabel ? ` (${measurementUnitLabel})` : ''}{' '}
                <Popover
                  content={
                    <div style={{ maxWidth: 260 }}>
                      Activates alerts and alarm when pressure goes below
                      this threshold.
                    </div>
                  }
                  trigger={['hover', 'click']}
                >
                  <Icon type="question-circle-o" className="pointer" />
                </Popover>
              </span>
            }
            validate={[isNumeric]}
            className="mr3-ns w-third-ns"
          />
          <Field
            name="deactivateThreshold"
            type="number"
            component={InputContainer}
            label={
              <span>
                Deactivate Threshold
                {measurementUnitLabel ? ` (${measurementUnitLabel})` : ''}{' '}
                <Popover
                  content={
                    <div style={{ maxWidth: 260 }}>
                      Deactivates alarm when pressure goes above this
                      threshold.
                    </div>
                  }
                  trigger={['hover', 'click']}
                >
                  <Icon type="question-circle-o" className="pointer" />
                </Popover>
              </span>
            }
            validate={[isNumeric]}
            className="w-third-ns"
          />
        </div>
        <div className="flex-ns">
          <Field
            name="audioAlert"
            component={Checkbox}
            type="checkbox"
            label={
              <span>
                Audio Alert{' '}
                <Popover
                  content={
                    <div style={{ maxWidth: 260 }}>
                      When on, the device plays an audible alert when the
                      activate threshold is crossed.
                    </div>
                  }
                  trigger={['hover', 'click']}
                >
                  <Icon type="question-circle-o" className="pointer" />
                </Popover>
              </span>
            }
            className="w-third-ns"
          />
        </div>
        {canViewAdminFields && (
          <div className="flex-ns">
            <Field
              name="toggleSign"
              component={Checkbox}
              type="checkbox"
              label={
                <span>
                  Toggle Sign (Admin only){' '}
                  <Popover
                    content={
                      <div style={{ maxWidth: 260 }}>
                        Inverts the sign of the pressure value shown on the
                        device's display.
                      </div>
                    }
                    trigger={['hover', 'click']}
                  >
                    <Icon type="question-circle-o" className="pointer" />
                  </Popover>
                </span>
              }
              className="w-third-ns"
            />
          </div>
        )}
        {canViewAdminFields && (
          <div>
            <Divider />
            <Legend>Alert Timing (Admin only)</Legend>
            <div className="flex-ns">
              <Field
                name="violationDelay"
                type="number"
                component={InputContainer}
                label={
                  <span>
                    Violation Delay (sec){' '}
                    <Popover
                      content={
                        <div style={{ maxWidth: 260 }}>
                          Window size, in seconds, used to detect a violation
                          of average readings.
                        </div>
                      }
                      trigger={['hover', 'click']}
                    >
                      <Icon type="question-circle-o" className="pointer" />
                    </Popover>
                  </span>
                }
                validate={[isInteger]}
                className="mr3-ns w-third-ns"
              />
              <Field
                name="cooldownDelayS"
                type="number"
                component={InputContainer}
                label={
                  <span>
                    Cooldown Delay (sec){' '}
                    <Popover
                      content={
                        <div style={{ maxWidth: 260 }}>
                          Window size, in seconds, used to detect a
                          restoration of average readings.
                        </div>
                      }
                      trigger={['hover', 'click']}
                    >
                      <Icon type="question-circle-o" className="pointer" />
                    </Popover>
                  </span>
                }
                validate={[isInteger]}
                className="w-third-ns"
              />
            </div>
            <div className="flex-ns">
              <Field
                name="audibleDelay"
                type="number"
                component={InputContainer}
                label={
                  <span>
                    Audible Delay (sec){' '}
                    <Popover
                      content={
                        <div style={{ maxWidth: 260 }}>
                          Delay, in seconds, before the buzzer is activated.
                        </div>
                      }
                      trigger={['hover', 'click']}
                    >
                      <Icon type="question-circle-o" className="pointer" />
                    </Popover>
                  </span>
                }
                validate={[isInteger]}
                className="mr3-ns w-third-ns"
              />
              <Field
                name="cancelDelay"
                type="number"
                component={InputContainer}
                label={
                  <span>
                    Cancel Delay (sec){' '}
                    <Popover
                      content={
                        <div style={{ maxWidth: 260 }}>
                          When the button is pressed, the delay before a
                          violation alert can be generated again.
                        </div>
                      }
                      trigger={['hover', 'click']}
                    >
                      <Icon type="question-circle-o" className="pointer" />
                    </Popover>
                  </span>
                }
                validate={[isInteger]}
                className="w-third-ns"
              />
            </div>
            <div className="flex-ns">
              <Field
                name="displayTimeoutS"
                type="number"
                component={InputContainer}
                label={
                  <span>
                    Display Timeout (sec){' '}
                    <Popover
                      content={
                        <div style={{ maxWidth: 260 }}>
                          Timeout for display. When on battery, both the LCD
                          and backlight will be powered off when the timer
                          expires.
                        </div>
                      }
                      trigger={['hover', 'click']}
                    >
                      <Icon type="question-circle-o" className="pointer" />
                    </Popover>
                  </span>
                }
                validate={[isInteger]}
                className="mr3-ns w-third-ns"
              />
            </div>
          </div>
        )}
        <div className="flex-ns">
          <Field
            name="measurementUnits"
            component={SelectContainer}
            label="Measurement Units"
            placeholder="Select measurement units"
            className="w-third-ns"
            filterable
            validate={[required]}
          >
            {SELECTABLE_MEASUREMENT_UNITS.map(x => (
              <Option value={x} key={x}>
                {PRESSURE_MONITOR_MEASUREMENT_UNITS[x]}
              </Option>
            ))}
          </Field>
        </div>
        {canViewAdminFields && (
          <div>
            <Divider />
            <Legend>Data Rates (Admin only)</Legend>
            <div className="flex-ns">
              <Field
                name="reportIntervalS"
                type="number"
                component={InputContainer}
                label="Report Interval (sec)"
                validate={[isInteger]}
                className="w-third-ns"
              />
            </div>
          </div>
        )}
        <Divider />
        <FormError error={error} />
        <div className="flex justify-between">
          <CancelButton defaultLocation="/inventory/all/pressure-monitors" />
          <Button text="Submit" type="submit" submitting={submitting} />
        </div>
      </Form>
    </section>
  )
}

EditPressureMonitorForm.propTypes = {
  submitAction: PropTypes.func.isRequired,
  assetLocations: PropTypes.arrayOf(PropTypes.object),
  handleSubmit: PropTypes.func.isRequired,
  submitting: PropTypes.bool.isRequired,
  isPressureMonitorLoading: PropTypes.bool,
  error: PropTypes.string,
  userRole: PropTypes.string,
  initialValues: PropTypes.object,
}

const mapStateToProps = ({ account }) => ({
  userRole: account.profile.role,
})

export default connect(mapStateToProps)(
  reduxForm({ form: 'EditPressureMonitorForm' })(EditPressureMonitorForm)
)
